import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePreviewRemakeKitAssetsMutation, useRemakeKitAssetsMutation } from '../../../../../redux/api/fymAPI';
import { RemakeKitAssetsPreview } from '../../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { kitRefusal } from '../assets/kitErrors';
import { assetMessages } from '../assets/assetMessages';
import { themeMessages } from './themeMessages';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { splitRemakeForBalance } from '../../credits/splitKitItemsForBalance';

interface UseRemakeStaleAssetsProps {
  accountId: number;
  catalogItemUuid: string;
  assetUuids: string[];
}

export interface RemakeStaleAssets {
  isOpen: boolean;
  /** The dry run answer: the cost. Undefined while it loads or after it failed. */
  preview: RemakeKitAssetsPreview | undefined;
  isChecking: boolean;
  isRemaking: boolean;
  /** One sentence for the dialog: the dry run or the call failed. */
  error: string | undefined;
  open: () => void;
  cancel: () => void;
  confirm: () => void;
}

/**
 * "Make them again" (spec §6.4, design q2b). Open asks the server for the cost with a dry run
 * (`dryRun: true` charges nothing); the dialog shows it (videos their credits, images free). Confirm
 * makes the new versions in one charge. When the balance (plan plus packs) cannot pay for every
 * video, confirm makes only the free images and opens the credit packs (g8) or the owner note (e4)
 * for the videos; a set of videos only opens them at once (owner rules 2026-10-06). A 402 for video credits opens the same packs
 * through the global upgrade modal. A busy answer and a missing master show one toast. The dialog
 * closes on success and on these refusals.
 */
export function useRemakeStaleAssets({
  accountId,
  catalogItemUuid,
  assetUuids,
}: UseRemakeStaleAssetsProps): RemakeStaleAssets {
  const intl = useIntl();
  const { showInfo, showSuccess } = useShowToast();
  const [previewRemake, previewState] = usePreviewRemakeKitAssetsMutation();
  const [remake, remakeState] = useRemakeKitAssetsMutation();
  const { canSpend, isShort, offerCredits } = useVideoCreditGate(accountId);
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<RemakeKitAssetsPreview>();
  const [error, setError] = useState<string>();

  const open = useCallback(() => {
    setIsOpen(true);
    setPreview(undefined);
    setError(undefined);
    previewRemake({ accountId, catalogItemUuid, assetUuids })
      .unwrap()
      .then((answer) => {
        // With images in the set, the dialog stays: the images are made, the videos wait (owner
        // rule 2026-10-06). With only videos, the packs open at once.
        const hasImages = answer.items.some((one) => one.credits === 0);
        if (!hasImages && !canSpend(answer.credits)) {
          setIsOpen(false);
          return;
        }
        setPreview(answer);
      })
      .catch((reason: unknown) => {
        console.error('Failed to check the cost of making the assets again', reason);
        setError(intl.formatMessage(themeMessages.remakeCheckFailed));
      });
  }, [accountId, assetUuids, canSpend, catalogItemUuid, intl, previewRemake]);

  const cancel = useCallback(() => {
    if (!remakeState.isLoading) {
      setIsOpen(false);
    }
  }, [remakeState.isLoading]);

  const confirm = useCallback(() => {
    // The free images always go; the videos go only when the balance pays for all of them.
    const split = preview === undefined ? null : splitRemakeForBalance(preview.items, isShort);
    const uuids = split === null ? assetUuids : split.make.map((item) => item.assetUuid);
    if (split !== null && uuids.length === 0) {
      setIsOpen(false);
      offerCredits();
      return;
    }
    setError(undefined);
    remake({ accountId, catalogItemUuid, assetUuids: uuids })
      .unwrap()
      .then((result) => {
        setIsOpen(false);
        if (split !== null && split.skipped.length > 0) {
          offerCredits();
          showInfo(intl.formatMessage(assetMessages.imagesOnly, { credits: split.skippedCredits }));
          return;
        }
        if (result.created.length > 0) {
          showSuccess(intl.formatMessage(themeMessages.remakeStarted, { count: result.created.length }));
        } else if (result.busy.length > 0) {
          showInfo(intl.formatMessage(assetMessages.busy));
        } else if ((result.skipped?.length ?? 0) > 0) {
          // Every asset needs the master, and the song has no ready master yet.
          showInfo(intl.formatMessage(assetMessages.noMaster));
        }
      })
      .catch((reason: unknown) => {
        const refusal = kitRefusal(reason);
        if (refusal === 'videoCredits') {
          setIsOpen(false);
          return;
        }
        if (refusal === 'busy') {
          setIsOpen(false);
          showInfo(intl.formatMessage(assetMessages.busy));
          return;
        }
        if (refusal === 'noMaster') {
          setIsOpen(false);
          showInfo(intl.formatMessage(assetMessages.noMaster));
          return;
        }
        console.error('Failed to make the assets again', reason);
        setError(intl.formatMessage(themeMessages.remakeFailed));
      });
  }, [accountId, assetUuids, catalogItemUuid, intl, isShort, offerCredits, preview, remake, showInfo, showSuccess]);

  return {
    isOpen,
    preview,
    isChecking: previewState.isLoading,
    isRemaking: remakeState.isLoading,
    error,
    open,
    cancel,
    confirm,
  };
}
